import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileUp, Download, Play, CheckCircle2, XCircle, Loader2, Truck } from "lucide-react";
import { toast } from "sonner";
import { appApi, formatApiErrorDetail, inr } from "@/lib/api";

const TEMPLATE =
  "customer_name,customer_phone,address,city,state,pincode,weight_kg,length_cm,breadth_cm,height_cm,invoice_value,payment_mode,description\n" +
  "Riya Das,9876543210,\"Flat 4B, Lake Road\",Kolkata,West Bengal,700029,0.8,20,15,10,1299,prepaid,Cotton kurti\n" +
  "Aman Verma,9812345678,\"22 MG Road\",Bengaluru,Karnataka,560001,1.2,25,20,12,2499,cod,Bluetooth speaker\n";

export default function BulkCsv() {
  const navigate = useNavigate();
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseId, setWarehouseId] = useState("");
  const [couriers, setCouriers] = useState(null);
  const [courierId, setCourierId] = useState("");
  const [couriersLoading, setCouriersLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState(null);
  const [rates, setRates] = useState({});
  const [parsing, setParsing] = useState(false);
  const [booking, setBooking] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    appApi.get("warehouses").then((d) => setWarehouses(d.warehouses || [])).catch(() => {});
  }, []);

  // App flow: pick warehouse → courier section appears BEFORE CSV upload
  const loadCouriers = async (whId) => {
    setCouriers(null);
    setCourierId("");
    setRows(null);
    setResult(null);
    setRates({});
    if (!whId) return;
    const wh = warehouses.find((w) => w.id === whId);
    if (!wh) return;
    setCouriersLoading(true);
    try {
      const d = await appApi.get("orders/bulk/rate-preview", {
        pickup_pincode: wh.pincode,
        delivery_pincode: wh.pincode,
        weight_kg: 1,
        payment_mode: "prepaid",
      });
      setCouriers(d.couriers || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setCouriers([]);
    } finally {
      setCouriersLoading(false);
    }
  };

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "shipzilla_bulk_orders_template.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const previewRowRates = async (parsed, pickupPincode, cid) => {
    const next = {};
    await Promise.all(
      parsed.filter((r) => r.valid).map(async (r) => {
        try {
          const d = await appApi.get("orders/bulk/rate-preview", {
            pickup_pincode: pickupPincode,
            delivery_pincode: r.data.pincode,
            weight_kg: parseFloat(r.data.weight_kg),
            payment_mode: r.data.payment_mode,
          });
          const match = (d.couriers || []).find((c) => c.courier_id === cid) || (d.couriers || [])[0];
          if (match) next[r.row_number] = match;
        } catch { /* leave empty */ }
      })
    );
    setRates(next);
  };

  const parseFile = async () => {
    if (!courierId) {
      toast.error("Please select a courier first.");
      return;
    }
    if (!file) {
      toast.error("Choose a CSV file first.");
      return;
    }
    setParsing(true);
    setResult(null);
    try {
      const csv_text = await file.text();
      const d = await appApi.post("orders/bulk/parse", { csv_text });
      const parsed = d.rows || [];
      setRows(parsed);
      const wh = warehouses.find((w) => w.id === warehouseId);
      if (wh) previewRowRates(parsed, wh.pincode, courierId);
      toast.success(`${parsed.filter((r) => r.valid).length} valid / ${parsed.length} rows parsed by the server.`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setParsing(false);
    }
  };

  const bookAll = async () => {
    if (!courierId) {
      toast.error("Please select a courier first.");
      return;
    }
    const valid = (rows || []).filter((r) => r.valid);
    if (!warehouseId) {
      toast.error("Select a pickup warehouse first.");
      return;
    }
    if (!valid.length) {
      toast.error("No valid rows to book.");
      return;
    }
    const courier = (couriers || []).find((c) => c.courier_id === courierId);
    const total = valid.reduce((s, r) => s + (rates[r.row_number]?.rate || 0), 0);
    if (!window.confirm(`Book ${valid.length} orders on ${courier?.courier_name}? Estimated wallet debit ${inr(total)}.`)) return;
    setBooking(true);
    try {
      const d = await appApi.post("orders/bulk/book", {
        warehouse_id: warehouseId,
        courier_id: courierId,
        rows: valid.map((r) => r.data),
      });
      setResult(d);
      const okCount = d.booked ?? d.success_count ?? (d.orders || []).length ?? valid.length;
      const failCount = d.failed ?? d.failure_count ?? (d.errors || []).length ?? 0;
      toast.success(`Bulk booking done — ${okCount} booked${failCount ? `, ${failCount} failed` : ""}.`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBooking(false);
    }
  };

  const validCount = rows ? rows.filter((r) => r.valid).length : 0;
  const totalRate = rows ? rows.filter((r) => r.valid).reduce((s, r) => s + (rates[r.row_number]?.rate || 0), 0) : 0;

  return (
    <div className="space-y-6" data-testid="bulk-csv">
      <div className="border-2 border-obsidian bg-white p-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="font-bold text-sm flex items-center gap-2"><FileUp className="w-4 h-4 text-acid" /> Bulk B2C booking via CSV</p>
          <p className="text-xs text-zinc-500 mt-1">
            Pick a warehouse, choose one courier for all shipments, upload CSV — the server validates every row and books them in one shot.
          </p>
        </div>
        <button onClick={downloadTemplate} data-testid="bulk-template-download"
          className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest border-2 border-obsidian px-4 py-2.5 bg-white hover:bg-acid hover:text-white transition-colors duration-150">
          <Download className="w-3.5 h-3.5" /> Download template
        </button>
      </div>

      <div className="border-2 border-obsidian bg-white p-5">
        <p className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">1 · Pickup warehouse (all rows)</p>
        <select value={warehouseId} onChange={(e) => { setWarehouseId(e.target.value); loadCouriers(e.target.value); }}
          data-testid="bulk-warehouse-select"
          className="mt-2 w-full border-2 border-obsidian bg-paper px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obsidian">
          <option value="">Select warehouse…</option>
          {warehouses.map((w) => (
            <option key={w.id} value={w.id}>{w.address_title} — {w.city} {w.pincode}</option>
          ))}
        </select>
      </div>

      {warehouseId && (
        <div className="border-2 border-obsidian bg-white p-5" data-testid="bulk-courier-section">
          <p className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500 flex items-center gap-2">
            <Truck className="w-4 h-4 text-acid" /> 2 · Courier for all shipments
            {couriers && <span className="text-zinc-400 normal-case">({couriers.length} available — rates indicative, final rate per row)</span>}
          </p>
          {couriersLoading && (
            <p className="mt-4 font-mono text-xs uppercase tracking-[0.3em] text-zinc-400 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading couriers…
            </p>
          )}
          {couriers && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mt-4 max-h-80 overflow-y-auto pr-1" data-testid="bulk-courier-list">
              {couriers.map((c) => (
                <button
                  type="button"
                  key={c.courier_id}
                  onClick={() => { setCourierId(c.courier_id); setRates({}); }}
                  data-testid={`bulk-courier-${c.courier_id}`}
                  className={`border-2 p-4 text-left transition-all duration-150 ${
                    courierId === c.courier_id
                      ? "border-obsidian bg-acid text-white shadow-[4px_4px_0px_0px_#0B1E3A]"
                      : "border-zinc-200 bg-paper hover:border-obsidian"
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <span className="w-8 h-8 flex items-center justify-center font-display font-black text-sm text-white border border-obsidian/20" style={{ backgroundColor: c.color || "#0B1E3A" }}>
                      {c.logo_letter}
                    </span>
                    <p className={`font-bold text-xs leading-tight ${courierId === c.courier_id ? "text-white" : "text-obsidian"}`}>{c.courier_name}</p>
                  </div>
                  <div className={`flex items-center justify-between mt-3 text-[11px] font-mono ${courierId === c.courier_id ? "text-white/85" : "text-zinc-500"}`}>
                    <span className="font-bold">{inr(c.rate)}</span>
                    <span>{c.eta_days}</span>
                  </div>
                  <p className={`text-[10px] font-mono mt-1 ${courierId === c.courier_id ? "text-white/85" : "text-zinc-400"}`}>
                    ★ {c.rating} · {c.cod_available ? "COD" : "Prepaid only"}
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="border-2 border-obsidian bg-white p-5">
        <p className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">3 · Upload CSV &amp; parse</p>
        <div className="flex gap-3 mt-2">
          <label data-testid="bulk-upload-drop"
            className="flex-1 flex items-center justify-center gap-3 border-2 border-dashed border-obsidian bg-paper px-4 py-2.5 text-sm cursor-pointer hover:bg-acid/20 transition-colors duration-150">
            <FileUp className="w-4 h-4 text-zinc-500" />
            <span>{file ? file.name : "Choose .csv file"}</span>
            <input type="file" accept=".csv,text/csv" className="hidden" data-testid="bulk-file-input"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setRows(null); setResult(null); }} />
          </label>
          <button onClick={parseFile} disabled={parsing || !file || !courierId} data-testid="bulk-parse-button"
            className="bg-obsidian text-sky-300 text-xs font-bold uppercase tracking-widest px-5 py-2.5 border-2 border-obsidian hover:bg-zinc-800 transition-colors duration-150 disabled:opacity-50">
            {parsing ? "Parsing…" : "Parse"}
          </button>
        </div>
        {!courierId && warehouseId && (
          <p className="text-[11px] font-mono font-bold uppercase tracking-widest text-blaze mt-2" data-testid="bulk-courier-warning">
            Please select a courier above before uploading
          </p>
        )}
      </div>

      {rows && (
        <div className="border-2 border-obsidian bg-white" data-testid="bulk-preview">
          <div className="flex items-center justify-between px-5 py-4 border-b-2 border-obsidian flex-wrap gap-3">
            <p className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">
              Preview — {validCount} valid / {rows.length} rows · est. debit <span className="text-obsidian">{inr(totalRate)}</span>
            </p>
            <button onClick={bookAll} disabled={booking || validCount === 0 || !courierId} data-testid="bulk-book-button"
              className="inline-flex items-center gap-2 bg-acid text-white text-xs font-bold uppercase tracking-widest px-6 py-3 border-2 border-obsidian shadow-[4px_4px_0px_0px_#0B1E3A] hover:shadow-none hover:translate-x-[4px] hover:translate-y-[4px] transition-all duration-150 disabled:opacity-50">
              <Play className="w-4 h-4" /> {booking ? "Booking…" : `Book ${validCount} Orders`}
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[860px]" data-testid="bulk-table">
              <thead>
                <tr className="border-b border-zinc-200 text-left">
                  {["#", "Customer", "Phone", "City", "Pincode", "Wt", "Mode", "Est. rate", "Status"].map((h) => (
                    <th key={h} className="px-4 py-2.5 text-[10px] font-mono font-bold uppercase tracking-widest text-zinc-400">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.row_number} className="border-b border-zinc-100 last:border-0" data-testid={`bulk-row-${r.row_number}`}>
                    <td className="px-4 py-2.5 font-mono text-xs text-zinc-400">{r.row_number}</td>
                    <td className="px-4 py-2.5 font-semibold">{r.data.customer_name || "—"}</td>
                    <td className="px-4 py-2.5 font-mono text-xs">{r.data.customer_phone}</td>
                    <td className="px-4 py-2.5 text-zinc-600">{r.data.city}</td>
                    <td className="px-4 py-2.5 font-mono text-xs">{r.data.pincode}</td>
                    <td className="px-4 py-2.5 font-mono text-xs">{r.data.weight_kg}kg</td>
                    <td className="px-4 py-2.5 font-mono text-xs uppercase">{r.data.payment_mode}</td>
                    <td className="px-4 py-2.5 font-mono text-xs font-bold">
                      {r.valid ? (rates[r.row_number] ? inr(rates[r.row_number].rate) : "…") : "—"}
                    </td>
                    <td className="px-4 py-2.5" data-testid={`bulk-status-${r.row_number}`}>
                      {r.valid ? (
                        <span className="inline-flex items-center gap-1.5 text-[11px] font-mono font-bold text-green-700">
                          <CheckCircle2 className="w-3.5 h-3.5" /> Ready
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-[11px] font-mono font-bold text-red-600">
                          <XCircle className="w-3.5 h-3.5" /> {r.error || "Invalid"}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {result && (
        <div className="border-2 border-obsidian bg-acid/20 p-6" data-testid="bulk-result">
          <p className="font-bold text-sm flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-green-700" /> Bulk booking complete
          </p>
          <pre className="mt-3 text-xs font-mono bg-white border border-zinc-200 p-4 overflow-x-auto max-h-48">{JSON.stringify(result, null, 2)}</pre>
          <button onClick={() => navigate("/dashboard/orders")} data-testid="bulk-view-orders"
            className="mt-4 inline-flex items-center gap-2 bg-obsidian text-sky-300 text-xs font-bold uppercase tracking-widest px-6 py-3 border-2 border-obsidian hover:bg-zinc-800 transition-colors duration-150">
            View Orders
          </button>
        </div>
      )}
      {booking && (
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-400 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Booking all rows on the server…
        </p>
      )}
    </div>
  );
}
